// The news lane: conflict-relevant reporting that has no place on the map.
//
// WHAT MAKES THIS A SEPARATE LANE RATHER THAN MORE FEED. A live event answers
// "what happened, where" — it has a coordinate, a kind, a pin, an age ramp and
// a situation it belongs to. A news item has none of those and never will: a
// head of state's denial, an economic consequence of the war, a medevac tally.
// Mixed into the event feed they would dilute exactly the signal the feed
// exists for, and grouped into situations they would attach a statement to a
// crater. So they get their own tab, and the map never hears about them.
//
// THE MAP CANNOT SHOW THESE EVEN BY MISTAKE, and that is by construction rather
// than by this module's care: migration 044 declares `news_item` with
// geometry_kinds '{none}', so the rows carry no geometry, and mvt_entities
// filters on `ec.geom IS NOT NULL`. Nothing here is load-bearing for that
// promise — which is the point of making it a storage property.

import type { EntityDto } from './api';

/** The entity type migration 044 creates. One definition, because "is this a
 *  news item" is asked by the pane split, the feed mode and the layers panel. */
export const NEWS_TYPE = 'news_item';

export function isNewsItem(e: EntityDto): boolean {
  return e.type === NEWS_TYPE;
}

/** The subject line — what several channels are all reporting. Falls back to
 *  the headline, matching llm.clean_news's own fallback, so a row is never
 *  blank because the model skipped a field. */
export function topicOf(e: EntityDto): string {
  return String(e.attrs.topic ?? '').trim()
    || String(e.attrs.headline ?? '').trim()
    || e.name
    || '(untitled)';
}

/** What THIS post said, as opposed to the subject it is about. */
export function headlineOf(e: EntityDto): string {
  return String(e.attrs.headline ?? '').trim()
    || String(e.attrs.summary ?? '').trim()
    || String(e.attrs.text ?? '').trim();
}

/** The parties the item is about. Rendered as words, never as a map layer —
 *  a news item's actors are its subject, not its location. */
export function actorsOf(e: EntityDto): string[] {
  const raw = e.attrs.actors;
  if (!Array.isArray(raw)) return [];
  return raw.map((a) => String(a ?? '').trim()).filter(Boolean).slice(0, 6);
}

/** The country the item CONCERNS. Deliberately named `contextOf` rather than
 *  anything positional: llm.clean_news is explicit that this is a label and
 *  never a location, and a helper called `placeOf` would invite exactly the
 *  misreading the lane is built to prevent. */
export function contextOf(e: EntityDto): string {
  return String(e.attrs.country ?? '').trim();
}

/** How many source posts are behind this item. The server keeps sourceCount in
 *  step on every merge; the sources array is the fallback for older rows. */
export function reportCountOf(e: EntityDto): number {
  const n = Number(e.attrs.sourceCount ?? 0);
  if (Number.isFinite(n) && n > 0) return n;
  const raw = e.attrs.sources;
  return Array.isArray(raw) && raw.length ? raw.length : 1;
}
